import { motion, AnimatePresence } from 'framer-motion';
import { X, Move } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const PORTRAIT_URL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_142de766-7447-4fb5-b920-10398996bff4/artifacts/0pp8u9ck_fondo-cyborg-8k-vertical.webp";
const SPEAKING_GIF_URL = "https://customer-assets-lqy194kg.emergentagent.net/job_alivan-ai/artifacts/cqwzxhqa_DS8Nt.gif";

const STORAGE_KEY = 'alivan-speaker-position';

const loadPosition = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return { x: 0, y: 0 };
};

const savePosition = (pos) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch (e) {}
};

const SpeakingIndicator = ({ visible, amplitude = 0, onStop }) => {
  const amp = Math.min(1, amplitude * 1.8);
  const constraintsRef = useRef(null);
  const [position, setPosition] = useState(() => loadPosition());
  const [isDragging, setIsDragging] = useState(false);

  // Update viewport constraint bounds on resize
  const [bounds, setBounds] = useState({ top: 0, left: 0, right: 0, bottom: 0 });

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Panel is roughly 224px wide by 260px tall (avatar + label + padding)
      const panelW = 224;
      const panelH = 260;
      // Base anchor is bottom-right at (right: 24, bottom: 96). We use x,y translation from that.
      // Allow moving anywhere in viewport. Compute how far we can go in each direction.
      const anchorRight = 24;
      const anchorBottom = 96;
      // From anchor position, x range: from -(w - panelW - anchorRight - 16) (to left edge) to +anchorRight-16 (to right edge)
      const leftBound = -(w - panelW - anchorRight - 8);
      const rightBound = anchorRight - 8;
      const topBound = -(h - panelH - anchorBottom - 8);
      const bottomBound = anchorBottom - 8;
      setBounds({ left: leftBound, right: rightBound, top: topBound, bottom: bottomBound });
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // Mouth opens vertically with amplitude
  const mouthOpenY = 0.15 + amp * 0.85;
  const mouthOpacity = 0.7 + amp * 0.3;

  return (
    <>
      {/* Invisible constraints layer covering the whole viewport */}
      <div
        ref={constraintsRef}
        className="fixed inset-0 pointer-events-none z-30"
        aria-hidden
      />
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: position.y + 60, x: position.x, scale: 0.9 }}
            animate={{ opacity: 1, y: position.y, x: position.x, scale: 1 }}
            exit={{ opacity: 0, y: position.y + 40, x: position.x, scale: 0.9 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            drag
            dragMomentum={false}
            dragElastic={0.05}
            dragConstraints={bounds}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={(_e, info) => {
              const next = {
                x: position.x + info.offset.x,
                y: position.y + info.offset.y
              };
              // Clamp to bounds
              next.x = Math.max(bounds.left, Math.min(bounds.right, next.x));
              next.y = Math.max(bounds.top, Math.min(bounds.bottom, next.y));
              setPosition(next);
              savePosition(next);
              setIsDragging(false);
            }}
            whileDrag={{ cursor: 'grabbing', scale: 1.03 }}
            className="fixed bottom-24 right-6 z-40 select-none"
            style={{ touchAction: 'none' }}
            data-testid="speaking-indicator"
          >
            <div className="relative" style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
              {/* Drag handle indicator (top-left) */}
              <div
                className="absolute -top-2 -left-2 p-1.5 bg-black/90 border border-neon-red/40 text-white/60 rounded-full pointer-events-none z-10"
                title="Arrastra para mover"
                data-testid="drag-handle"
              >
                <Move className="w-3 h-3" />
              </div>

              {/* Outer pulsing glow ring */}
              <div
                className="absolute -inset-6 rounded-full pointer-events-none"
                style={{
                  background: 'radial-gradient(circle, rgba(255, 0, 51, 0.35) 0%, transparent 70%)',
                  filter: `blur(${16 + amp * 24}px)`,
                  opacity: 0.4 + amp * 0.6,
                  transition: 'opacity 60ms linear, filter 60ms linear'
                }}
              />

              {/* Main avatar container - shows animated GIF (real Alivan) while speaking */}
              <div
                className="w-48 h-48 sm:w-56 sm:h-56 overflow-hidden relative border-2"
                style={{
                  borderColor: `rgba(255, 0, 51, ${Math.max(0.6, amp)})`,
                  transform: `scale(${1 + amp * 0.03})`,
                  transition: 'transform 60ms linear, border-color 60ms linear',
                  boxShadow: `0 0 ${20 + amp * 40}px rgba(255, 0, 51, ${0.4 + amp * 0.5})`,
                  borderRadius: '12px'
                }}
              >
                <img
                  src={SPEAKING_GIF_URL}
                  alt="Alivan hablando"
                  className="w-full h-full object-cover pointer-events-none"
                  draggable={false}
                />

                {/* Subtle red gradient overlay to blend with theme */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(circle at center, transparent 40%, rgba(255, 0, 51, ${0.1 + amp * 0.2}) 100%)`,
                    mixBlendMode: 'screen',
                    transition: 'opacity 60ms linear'
                  }}
                />

                {/* Voice waveform bars overlay at bottom */}
                <div
                  className="absolute bottom-2 left-0 right-0 pointer-events-none flex items-end justify-center gap-1"
                  style={{
                    height: '16%',
                    opacity: 0.5 + amp * 0.5
                  }}
                >
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => {
                    const wave = 0.4 + Math.sin(Date.now() / 120 + i * 0.7) * 0.3;
                    return (
                      <div
                        key={i}
                        className="w-[3px] bg-neon-red"
                        style={{
                          height: `${25 + amp * 75 * wave}%`,
                          transition: 'height 60ms linear',
                          boxShadow: '0 0 8px rgba(255, 0, 51, 0.95)',
                          borderRadius: '2px'
                        }}
                      />
                    );
                  })}
                </div>

                {/* Scan-line effect for cyberpunk feel */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-10"
                  style={{
                    background: 'repeating-linear-gradient(0deg, transparent 0px, transparent 2px, rgba(255, 0, 51, 0.3) 2px, rgba(255, 0, 51, 0.3) 3px)',
                    mixBlendMode: 'overlay'
                  }}
                />
              </div>

              {/* Label */}
              <div className="mt-3 flex items-center justify-center gap-2 pointer-events-none">
                <div
                  className="w-2 h-2 rounded-full bg-neon-red"
                  style={{
                    transform: `scale(${1 + amp * 1.5})`,
                    opacity: 0.6 + amp * 0.4,
                    boxShadow: `0 0 ${4 + amp * 12}px rgba(255, 0, 51, 0.9)`,
                    transition: 'all 60ms linear'
                  }}
                />
                <p className="font-jetbrains text-xs text-white tracking-widest uppercase">
                  Alivan hablando
                </p>
              </div>

              {/* Stop button */}
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onStop();
                }}
                data-testid="stop-speaking-button"
                className="absolute -top-2 -right-2 p-1.5 bg-black/90 border border-neon-red/40 text-white/70 hover:text-white hover:border-neon-red rounded-full transition-all z-10"
                title="Detener"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SpeakingIndicator;
